import React from 'react';
/**
 * Feedback Section Builder — Constructs accordion sections for the detail phase.
 *
 * Extracted from FeedbackDisplay.tsx (691→~300 lines) during CL-1 prep s62.
 * Co-located in practice/ domain (CL-1 s64).
 */

import type { Question } from '../../../content';
import { DjayAvatar, type DjayVariant } from '../../../mascot';
import { VisualAidFactory, shouldShowVisualAid } from '../../../visuals';
import type { SupportContent } from '../../../support';
import { ExplanationContent } from './ExplanationContent';
import type { AccordionSection } from './FeedbackAccordion';
import { formatMoneyDisplay, type MoneyToken } from './feedbackAnswerFormatters';
// ── Types ────────────────────────────────────────────────────
interface SectionBuilderInput {
  question: Question;
  wasCorrect: boolean;
  resolvedSupport: SupportContent;
  distractorExplanation?: string;
  distractorInHeader: boolean;
  selectedVariant: DjayVariant;
  levelUp?: {
    level: number;
    xp: number;
    title: string;
  } | null;
  userMoneyTokens: MoneyToken[] | null;
  correctCents: number | null;
}
// ── Helpers ──────────────────────────────────────────────────
function getVisualHeaderText(resolvedSupport: SupportContent, isMoneyAssembler: boolean): string {
  if (isMoneyAssembler) return 'Vergelijk de munten';
  if (!resolvedSupport.visual) return 'Kijk eens!';
  switch (resolvedSupport.visual.type) {
    case 'fraction':
      return 'Kijk, zo zie je het!';
    case 'geo':
      return 'Kijk, zo meet je het!';
    case 'rule':
      return 'Onthoud deze regel!';
    case 'column':
      return 'Stap voor stap!';
    default:
      return 'Kijk eens!';
  }
}
function getMoneyStyles(cents: number): string {
  if (cents >= 500) return 'w-14 h-9 rounded-lg bg-gradient-to-br from-slate-400 to-slate-600 text-white font-bold text-xs shadow-sm';
  if (cents >= 100) return 'w-9 h-9 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 text-amber-900 font-bold text-xs shadow-sm';
  if (cents >= 50) return 'w-9 h-9 rounded-full bg-gradient-to-br from-yellow-300 to-yellow-500 text-yellow-900 font-bold text-[10px] shadow-sm';
  return 'w-7 h-7 rounded-full bg-gradient-to-br from-orange-400 to-orange-500 text-orange-900 font-bold text-[9px] shadow-sm';
}
function getDenominationDisplay(cents: number): string {
  return cents >= 100 ? `€${cents / 100}` : `${cents}c`;
}
function MoneyTokenGrid({
  tokens,
  wasCorrect,
  correctCents
}: {
  tokens: MoneyToken[];
  wasCorrect: boolean;
  correctCents: number;
}) {
  return <div className="space-y-4" data-id="element-6609">
      <div data-id="element-6610">
        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-6611">
          {wasCorrect ? '✅ Jouw antwoord' : '❌ Jouw antwoord'}
        </h4>
        <div className="bg-white rounded-xl p-3 border border-slate-200" data-id="element-6612">
          <div className="flex flex-wrap gap-2" data-id="element-6613">
            {tokens.map((token, idx) => <div key={idx} className="flex items-center gap-1" data-id="element-6614">
                <div className={`flex items-center justify-center ${getMoneyStyles(token.valueCents)}`} data-id="element-6615">
                  <span data-id="element-6616">{getDenominationDisplay(token.valueCents)}</span>
                </div>
                {token.count > 1 && <span className="text-xs font-bold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded-md" data-id="element-6617">
                    ×{token.count}
                  </span>}
              </div>)}
          </div>
        </div>
      </div>
      {!wasCorrect && <div data-id="element-6618">
          <h4 className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-2" data-id="element-6619">
            ✅ Correct antwoord
          </h4>
          <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-200" data-id="element-6620">
            <p className="text-lg font-black text-emerald-700" data-id="element-6621">
              {formatMoneyDisplay(correctCents)}
            </p>
          </div>
        </div>}
    </div>;
}
// ── Main Builder ─────────────────────────────────────────────
export function buildAccordionSections(input: SectionBuilderInput): AccordionSection[] {
  const {
    question,
    wasCorrect,
    resolvedSupport,
    distractorExplanation,
    distractorInHeader,
    selectedVariant,
    levelUp,
    userMoneyTokens,
    correctCents
  } = input;
  const isMoneyAssembler = question.questionType === 'money';
  const supportVisuals = question.supportAids || [];
  const showSupportVisuals = supportVisuals.some(aid => shouldShowVisualAid(aid, wasCorrect));
  const sections: AccordionSection[] = [];
  // 0. Level Up Info
  if (levelUp && wasCorrect) {
    sections.push({
      id: 'levelup',
      icon: <span className="text-lg" data-id="element-6622">🌟</span>,
      title: `Level ${levelUp.level} — ${levelUp.title}`,
      subtitle: `Je hebt nu ${levelUp.xp} XP!`,
      content: <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 text-center" data-id="element-6623">
          <p className="text-amber-800 font-bold text-lg mb-1" data-id="element-6624">
            Gefeliciteerd met je nieuwe level! 🎉
          </p>
          <p className="text-amber-700 font-medium text-sm" data-id="element-6625">
            Blijf oefenen om nog verder te groeien.
          </p>
        </div>
    });
  }
  // 1. Explanation
  const accordionDistractor = distractorInHeader ? undefined : distractorExplanation;
  if (resolvedSupport.explanation || accordionDistractor) {
    sections.push({
      id: 'explanation',
      icon: <DjayAvatar size="sm" emotion={wasCorrect ? 'happy' : 'pointing'} variant={selectedVariant} animate={false} data-id="element-6626" />,
      title: wasCorrect ? 'Waarom dit klopt' : 'Meester Djay legt uit',
      subtitle: wasCorrect ? undefined : 'Tik om te bekijken',
      pulse: !wasCorrect,
      content: <ExplanationContent explanation={resolvedSupport.explanation || ''} distractorExplanation={accordionDistractor} isCorrect={wasCorrect} solutionSteps={resolvedSupport.solutionSteps} data-id="element-6627" />
    });
  }
  // 2. Visual Aids
  if (!isMoneyAssembler && showSupportVisuals) {
    sections.push({
      id: 'visual',
      icon: <DjayAvatar size="sm" emotion="pointing" variant={selectedVariant} animate={false} data-id="element-6628" />,
      title: getVisualHeaderText(resolvedSupport, isMoneyAssembler),
      content: <div className="space-y-4" data-id="element-6629">
          {supportVisuals.filter(aid => shouldShowVisualAid(aid, wasCorrect)).map((aid, index) => <VisualAidFactory key={index} visualAid={aid} isCorrect={wasCorrect} mode="support" data-id="element-6630" />)}
        </div>
    });
  }
  // 3. Money Assembler Feedback
  if (isMoneyAssembler && userMoneyTokens && correctCents !== null) {
    sections.push({
      id: 'money',
      icon: <span className="text-lg" data-id="element-6631">🪙</span>,
      title: wasCorrect ? 'Jouw munten' : 'Vergelijk de munten',
      content: <MoneyTokenGrid tokens={userMoneyTokens} wasCorrect={wasCorrect} correctCents={correctCents} data-id="element-6632" />
    });
  }
  return sections;
}